import React, { useMemo, useState } from 'react';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { Table } from 'tea-component';
import { useIntl, getLocale } from 'umi';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import type { RankTrendTableCProps } from '@intelligence/types/topchart';
import { addV2Prefix } from '@/pages/intelligence/utils/commonUtils';
import ARightClickToJump from '@/pages/intelligence/components/ARightClickToJump';
import defaultIcon from '@/pages/intelligence/assets/svg/flag/default.svg';
import { getCountryFlag } from '@home/_config/countries';
import { getMarketKeyList } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/utils';
import { MetricsTableCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/MetricsTableCloums';

const { pageable, scrollable, autotip } = Table.addons;

const TableContent = function (props: RankTrendTableCProps) {
  const {
    tableData,
    entityType,
    pageIndex,
    setPageIndex,
    setTableSelection,
    tableSelection,
    changeMarketProfileAll,
    selectAllKeys,
    regionsCountries,
    onSortChage,
    pageSortAttr = {},
    setIsOperateCheckbox,
    getLineChartData,
    isTableNodata = false,
    radioType,
    rankType,
    reqDateObject,
    dataSourcesColumns,
    pageId,
    orderDevice,
    onTagClick,
  } = props;

  // console.log('TableContentTableContent', props);

  const isAvg = rankType === 'avg_ranking';
  const bubTitle = useMemo(() => {
    let defaultStr = 'dbi_segment_rank_trend_avg';
    if (rankType !== 'avg_ranking') {
      defaultStr =
        radioType === 'dod' ? 'dbi_segment_rank_trend_dod' : 'dbi_segment_rank_trend_period';
    }
    return defaultStr;
  }, [radioType, rankType]);
  // const { currentSortValue = 'desc' } = pageSortAttr;
  const { sortKey = '', currentSortValue = 'desc' } = pageSortAttr;
  const { formatMessage } = useIntl();
  const isEN = getLocale() === 'en-US';
  const allMarkets = [
    ...(regionsCountries?.map((area: any) => area.country_set).flat() || []),
    { country_code: 'global', country_name_ch: '全球', country_name_en: 'Global' },
    { country_code: 'Global', country_name_ch: '全球', country_name_en: 'Global' },
  ];
  // 跳转到游戏详情
  const gotoDetailsPage = (val: any, _jump = true) => {
    const path = `/intelligence/MarketProfile/Overview/detail?marketName=${val.market}&marketNameEn=${val.marketEn}&pageId=${entityType}`;
    return path;
  };
  const changeMpTableSort = function (e: SortByItemType) {
    onSortChage(e);
    setPageIndex(1);
  };

  const [pageSize, setPageSize] = useState(50);

  // 如果有主动传值无数据，则使用空数组，否则使用原数据
  const tableDataCur = useMemo(() => {
    return isTableNodata ? [] : tableData;
  }, [isTableNodata, tableData]);

  const getMetricCloums = () => {
    let metricCloums: any[] = [];
    // const titles = getPCConsoleCloumsByPageId(entityType, pageId);
    dataSourcesColumns.forEach((t) => {
      metricCloums.push(
        MetricsTableCloums({
          /** MetricsTable页只有降序功能 */
          onlySimpleSort: false, // pageId === 'MetricsTable' ? true : false,
          renderType: t?.renderType,
          width: 240,
          width_cn: 180,
          icon: t?.icon,
          orderDataSource: t?.metricValue ?? t?.dataSource,
          dataSource: t?.dataSource, // source === 'ogdb' ? 'databarin' : source,
          title: t?.textKey,
          textContent: t?.info,
          tipText: t?.iconInfo,
          key: `${t?.value}_${t?.dataSource}`,
          sortMetric: t?.value,
          metricKey: t?.metricKey || t?.value,
          currentSortValue,
          currentSortKey: sortKey,
          onSortChage,
          dateType: 'day',
          pageId,
          getMetricValueOfCvm: (cvm, metricKey) => {
            const metricValueList = cvm?.metrics || [];
            if (metricValueList?.length > 0) {
              return metricValueList.find((item: any) => item?.metric === metricKey);
            }
            return {};
          },
          formatMessage,
          sortByList: [],
          disabledSort: t?.disabled,
          orderDevice,
          dataSourceType: t?.dataSourceType || t?.dataSource,
          isNoDropdown: t?.isNoDropdown,
          entityType,
          // steam tag、game mode 列单元格中点击 tag 修改值并请求
          onTagClick,
          svgTipIsNoMore: t?.isNoMore,
          svgTipIsNewMore: t?.newMore,
          isAvgRankTrend: isAvg,
          bubTitleRankTrend: bubTitle,
          reqDateObject,
          align: 'right',
        }),
      );
    });
    metricCloums = metricCloums.filter(
      (ele) => ele.key !== 'meta_score_metacriticMetaCritic Score',
    );
    return metricCloums;
  };

  const columns: any[] = [
    {
      key: 'select',
      width: 37,
      fixed: 'left',
      header: () => (
        <TableCheckboxHeader
          disableChckekList={[]}
          selectList={tableSelection}
          tableSelectionAll={selectAllKeys}
          onChange={(params, type) => {
            // console.log(params, 'params');
            // console.log(type, 'dddd');
            setIsOperateCheckbox(true);
            changeMarketProfileAll({ type });
          }}
        />
      ),
      render: (cvm: any) => (
        <TableCheckboxRender
          selectList={tableSelection}
          cvm={{ ...(cvm || {}), gameId: cvm?.market }}
          onChange={(list) => {
            // console.log(params,'params');
            // console.log(list, 'list');
            setIsOperateCheckbox(true);
            setTableSelection(list);
            getLineChartData(getMarketKeyList(list));
          }}
        />
      ),
    },
    {
      key: 'market',
      header: (
        <div className="dbi-marketprofile-table-title">
          <span>#</span> {formatMessage({ id: 'dbi_countries' })}
        </div>
      ),
      width: 240,
      fixed: 'left',
      render: (item: any) => {
        const href = gotoDetailsPage(item, false);
        const imgUrl = getCountryFlag(item.market);
        return (
          <ARightClickToJump href={addV2Prefix(href)} style={{ display: 'inline' }}>
            <div
              className="dbi-marketprofile-table-cont"
              onClick={() => {
                gotoDetailsPage(item);
              }}
            >
              <span className="dbi-marketprofile-table-rank">{item?.rank}</span>
              <div className="dbi-marketprofile-table-img">
                {imgUrl ? (
                  <img
                    className={'normalFlag'}
                    src={imgUrl}
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.src = defaultIcon;
                    }}
                  />
                ) : (
                  <></>
                )}
              </div>
              <span className={`dbi-marketprofile-table-text`}>
                {allMarkets?.find((market: any) => market.country_code === item.market)?.[
                  isEN ? 'country_name_en' : 'country_name_ch'
                ] || item.market}
              </span>
            </div>
          </ARightClickToJump>
        );
      },
    },
    ...getMetricCloums(),
  ];
  const handleOnChangePageIng = (value: any) => {
    setPageSize(value.pageSize);
    setPageIndex?.(value.pageIndex);
  };

  return (
    <Table
      className={
        'tablecard-box dbi-table-selectable dbi-table-sticky dbi-user-guide__market-profile__table'
      } // 解决冲突保留的
      bordered="all"
      records={tableDataCur || []}
      columns={columns}
      addons={[
        pageable({
          pageSize,
          pageIndex,
          onPagingChange: handleOnChangePageIng,
        }),
        scrollable({
          minWidth: 400,
        }),
        autotip({
          emptyText: formatMessage({ id: 'dbi_detail_no_data_under' }),
        }),
      ]}
    />
  );
};
export default TableContent;
